import { httpClient, unwrap, unwrapPaginated } from '@/services/httpClient';
import type {
  EntryPayload, PeriodSummaryRow, ProjectTimeRow, TaskTimeRow, TimeEntry, TimeTrackingSettings,
  Timesheet, TodaySummary, WeekDetail,
} from '@/features/time-tracking/types';

export const timeTrackingApi = {
  current: () => unwrap<TimeEntry | null>(httpClient.get('/time-tracking/timer/current')),
  start: (payload: { task_id: number; description?: string; is_billable?: boolean; hourly_rate?: number }) =>
    unwrap<TimeEntry>(httpClient.post('/time-tracking/timer/start', payload)),
  stop: () => unwrap<TimeEntry>(httpClient.post('/time-tracking/timer/stop')),
  reportIdle: (idleSeconds: number) => unwrap<TimeEntry | null>(httpClient.post('/time-tracking/timer/idle', { idle_seconds: idleSeconds })),
  today: () => unwrap<TodaySummary>(httpClient.get('/time-tracking/today')),

  entries: (page: number, filters?: { taskId?: number; projectId?: number; userId?: number; isBillable?: boolean; from?: string; to?: string }) =>
    unwrapPaginated<TimeEntry>(httpClient.get('/time-tracking/entries', {
      params: {
        page, per_page: 20, task_id: filters?.taskId, project_id: filters?.projectId, user_id: filters?.userId,
        is_billable: filters?.isBillable === undefined ? undefined : filters.isBillable ? '1' : '0',
        from: filters?.from, to: filters?.to,
      },
    })),
  createEntry: (payload: EntryPayload) => unwrap<TimeEntry>(httpClient.post('/time-tracking/entries', payload)),
  updateEntry: (id: number, payload: Partial<EntryPayload>) => unwrap<TimeEntry>(httpClient.put(`/time-tracking/entries/${id}`, payload)),
  removeEntry: (id: number) => unwrap<null>(httpClient.delete(`/time-tracking/entries/${id}`)),

  getSettings: () => unwrap<TimeTrackingSettings>(httpClient.get('/time-tracking/settings')),
  updateSettings: (payload: Partial<TimeTrackingSettings>) => unwrap<TimeTrackingSettings>(httpClient.put('/time-tracking/settings', payload)),

  week: (weekStart?: string) => unwrap<WeekDetail>(httpClient.get('/time-tracking/timesheets/week', { params: { week: weekStart } })),
  myTimesheets: () => unwrap<Timesheet[]>(httpClient.get('/time-tracking/timesheets')),
  submitWeek: (weekStart: string, notes?: string) => unwrap<Timesheet>(httpClient.post(`/time-tracking/timesheets/${weekStart}/submit`, { notes })),

  teamTimesheets: (status?: string) => unwrap<Timesheet[]>(httpClient.get('/time-tracking/timesheets/team', { params: { status } })),
  approveTimesheet: (id: number) => unwrap<Timesheet>(httpClient.post(`/time-tracking/timesheets/${id}/approve`)),
  rejectTimesheet: (id: number, reason?: string) => unwrap<Timesheet>(httpClient.post(`/time-tracking/timesheets/${id}/reject`, { reason })),

  projectTimeReport: (from?: string, to?: string) =>
    unwrap<ProjectTimeRow[]>(httpClient.get('/time-tracking/reports/project-time', { params: { from, to } })),
  taskTimeReport: (from?: string, to?: string, projectId?: number) =>
    unwrap<TaskTimeRow[]>(httpClient.get('/time-tracking/reports/task-time', { params: { from, to, project_id: projectId } })),
  weeklySummary: (weeksBack?: number, userId?: number) =>
    unwrap<PeriodSummaryRow[]>(httpClient.get('/time-tracking/reports/weekly-summary', { params: { weeks_back: weeksBack, user_id: userId } })),
  monthlySummary: (monthsBack?: number, userId?: number) =>
    unwrap<PeriodSummaryRow[]>(httpClient.get('/time-tracking/reports/monthly-summary', { params: { months_back: monthsBack, user_id: userId } })),

  aiInsights: (days?: number, userId?: number) =>
    unwrap<{ insights: string | null; note?: string }>(httpClient.post('/time-tracking/ai/insights', { days, user_id: userId })),
};
